import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { faqs, siteConfig } from "@/mocks/site";
import { categories } from "@/mocks/categories";
import { buildBreadcrumbSchema } from "@/lib/seo";
import { SITE_URL } from "@/mocks/site";

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const jsonLd = [
    buildBreadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "常见问题", path: "/faq" },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "常见问题",
      url: `${SITE_URL}/faq`,
      inLanguage: "zh-CN",
    },
  ];

  return (
    <>
      <Seo
        title="常见问题 - 黑料看点更新节奏与订阅说明"
        description="黑料看点常见问题解答：更新频率、今日看点与主题分类的区别、内容筛选标准、订阅方式与投稿渠道，一次看清所有疑问。"
        keywords="黑料看点常见问题,黑料看点更新时间,看点订阅,内容筛选"
        path="/faq"
        jsonLd={jsonLd}
      />

      <section className="bg-background-50 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 pt-6 pb-8 md:pt-8 md:pb-10">
          <Breadcrumbs items={[{ name: "常见问题" }]} />
          <h1 className="mt-6 font-heading text-3xl md:text-4xl text-foreground-950">常见问题</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
            关于更新节奏、内容筛选与订阅方式，这里整理了读者最常问到的问题。如果没有找到答案，欢迎直接联系我们。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-8">
              <div className="rounded-lg bg-background-100 border border-background-200 divide-y divide-background-200">
                {faqs.map((item, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div key={item.q}>
                      <h2>
                        <button
                          type="button"
                          onClick={() => setOpenIndex(isOpen ? null : index)}
                          aria-expanded={isOpen}
                          className="w-full flex items-start justify-between gap-4 text-left px-5 md:px-7 py-5 md:py-6 cursor-pointer"
                        >
                          <span className="flex items-start gap-3">
                            <span className="font-heading text-sm text-primary-600 mt-0.5 shrink-0">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <span className="text-sm md:text-base font-medium text-foreground-950">{item.q}</span>
                          </span>
                          <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-lg text-primary-600 shrink-0 mt-0.5`}></i>
                        </button>
                      </h2>
                      {isOpen && (
                        <div className="px-5 md:px-7 pb-5 md:pb-6 -mt-1">
                          <p className="text-sm leading-[1.9] text-foreground-600 pl-8 md:pl-9">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 rounded-lg bg-accent-100 border border-accent-200 p-6 flex flex-col sm:flex-row sm:items-center gap-5">
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-500 text-accent-950 shrink-0">
                  <i className="ri-question-answer-line text-xl"></i>
                </span>
                <div className="flex-1">
                  <p className="text-base font-medium text-accent-900">还有其他问题？</p>
                  <p className="mt-1 text-sm leading-relaxed text-accent-900/85">
                    直接留言告诉我们，通常会在一个工作日内回复。也可以发邮件到 {siteConfig.contactEmail}。
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-md bg-foreground-950 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  联系我们 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-950 pb-4 border-b border-background-200">
                  快捷入口
                </h2>
                <ul className="mt-3 space-y-1">
                  <li>
                    <Link to="/today" className="flex items-center justify-between py-2 text-sm text-foreground-700 hover:text-primary-700 transition-colors">
                      <span className="flex items-center gap-2"><i className="ri-fire-line text-foreground-500"></i>今日看点</span>
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </Link>
                  </li>
                  <li>
                    <Link to="/topics" className="flex items-center justify-between py-2 text-sm text-foreground-700 hover:text-primary-700 transition-colors">
                      <span className="flex items-center gap-2"><i className="ri-layout-grid-line text-foreground-500"></i>主题分类</span>
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="flex items-center justify-between py-2 text-sm text-foreground-700 hover:text-primary-700 transition-colors">
                      <span className="flex items-center gap-2"><i className="ri-information-line text-foreground-500"></i>关于我们</span>
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-950 pb-4 border-b border-background-200">
                  按分类查看
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {categories.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/topic/${item.slug}`}
                      className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}